import React from 'react'
import { UseFormRegister, FieldError, Path, FieldValues } from 'react-hook-form'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FormTextareaProps<TFieldValues extends FieldValues> {
  label: string
  name: Path<TFieldValues>
  register: UseFormRegister<TFieldValues>
  error?: FieldError
  required?: boolean
  placeholder?: string
  disabled?: boolean
  helperText?: string
  className?: string
  rows?: number
}

export function FormTextarea<TFieldValues extends FieldValues>({
  label,
  name,
  register,
  error,
  required,
  placeholder,
  disabled,
  helperText,
  className,
  rows = 4,
}: FormTextareaProps<TFieldValues>) {
  return (
    <div className={className}>
      <label htmlFor={String(name)} className="block text-sm font-medium text-brand-gray mb-2">
        {label} {required && <span className="text-error">*</span>}
      </label>

      <textarea
        id={String(name)}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'w-full px-4 py-3 bg-brand-dark border rounded-monumenta text-white placeholder-brand-gray',
          'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all',
          error ? 'border-error' : 'border-brand-gray/30'
        )}
        {...register(name)}
      />

      {error && (
        <div className="flex items-center gap-2 mt-2 text-error text-sm">
          <AlertCircle className="w-4 h-4" />
          {error.message}
        </div>
      )}

      {helperText && !error && (
        <p className="text-xs text-brand-gray mt-1">{helperText}</p>
      )}
    </div>
  )
}
